import { DefaultButton, FocusTrapZone, IconButton, Layer, mergeStyleSets, Overlay, Popup, Text } from "@fluentui/react";
import i18next from "../../i18n/i18n";

interface Props {
    showWarningFlag: boolean;
    warningMsg: string | undefined;
    onClose: () => void;
}

/**
 * A dialog component to display information about unavailable products.
 * It will only be visible if showWarningFlag is true.
 * The dialog will display a warning message and provide a close button.
 * When the close button is clicked, the onClose callback will be called.
 * @param {boolean} showWarningFlag - Whether the dialog should be visible.
 * @param {string | undefined} warningMsg - The warning message to be displayed.
 * @param {() => void} onClose - The callback to be called when the close button is clicked.
 * @returns {React.ReactElement} - The dialog component.
 */
export const SelectedProductUnAvailableInfoMsgDialog = ({ showWarningFlag, warningMsg, onClose }: Props) => {
    const popupStyles = mergeStyleSets({
        root: {
            background: "rgba(0, 0, 0, 0.2)",
            bottom: "0",
            left: "0",
            position: "fixed",
            right: "0",
            top: "0",
            zIndex: 1000
        },
        content: {
            background: "white",
            left: "50%",
            maxWidth: "600px",
            padding: "0 0 2em",
            position: "absolute",
            top: "50%",
            transform: "translate(-50%, -50%)",
            borderRadius: 8,
            boxShadow: "0 4px 16px rgba(0,0,0,0.2)"
        }
    });

    const handleClose = () => {
        onClose();
    };

    if (!showWarningFlag) return null;

    return (
        <Layer>
            <Popup className={popupStyles.root} role="dialog" aria-modal="true" onDismiss={handleClose}>
                <Overlay onClick={handleClose} />
                <FocusTrapZone>
                    <div role="document" className={popupStyles.content}>
                        <div style={{ padding: "12px 16px 4px" }}>
                            <IconButton style={{ color: "red" }} iconProps={{ iconName: "WarningSolid" }} aria-label="Warning" />
                            <IconButton
                                style={{ color: "black", float: "right" }}
                                iconProps={{ iconName: "Cancel" }}
                                aria-label={i18next.t("chat.close")}
                                onClick={handleClose}
                            />
                            <span style={{ display: "block", padding: "5px 0", fontSize: 15, fontWeight: "bold" }}>
                                <Text>{warningMsg}</Text>
                            </span>
                        </div>
                        <div style={{ textAlign: "center", marginTop: "8px" }}>
                            <DefaultButton style={{ borderRadius: 6, padding: "4px 18px" }} onClick={handleClose}>
                                {i18next.t("chat.ok")}
                            </DefaultButton>
                        </div>
                    </div>
                </FocusTrapZone>
            </Popup>
        </Layer>
    );
};
